import { SearchField } from '../search/SearchComponents';
import { ReusableModal } from '../modals/ReusableModal';
import React, { useEffect, useState } from 'react';
import { useDebounce } from '../helpers/hooks';
import { CapacityComponent } from '@/types';
import {
  useGetCapacityComponentsQuery,
  useGetTSPsQuery,
} from '../services/edgeApi';
import { AdvancedFiltersState } from '../filters/advancedFiltersSlice';
import { LoadingList } from '../helpers/loading';
import { useSelector } from 'react-redux';
import { selectTsps } from '../map/slice/mapSelectors';

export type CapacityComponentSearchProps = {
  isSearching: boolean;
  onClose: () => void;
  onSelect: (component: CapacityComponent) => void;
  filters: Partial<AdvancedFiltersState['capacityComponents']>;
};

type SearchTsp = {
  id: number;
  name: string;
};

export const CapacityComponentSearch: React.FC<
  CapacityComponentSearchProps
> = ({ isSearching, onClose, onSelect, filters }) => {
  const [searchQuery, setSearchQuery] = useState(filters?.textSearch ?? '');
  const [selectedTspId, setSelectedTspId] = useState<string>('');
  const [selectedCapacityType, setSelectedCapacityType] = useState<
    '' | 'POINT' | 'SEGMENT'
  >('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  useGetTSPsQuery(undefined);
  const tsps = useSelector(selectTsps) as Record<string, SearchTsp>;

  useEffect(() => {
    setSearchQuery(filters?.textSearch ?? '');
  }, [filters?.textSearch]);

  return (
    <ReusableModal
      header="Search for Capacity Components"
      isOpen={isSearching}
      onClose={onClose}
      size="lg"
    >
      <SearchField
        value={searchQuery}
        onChange={(value) => setSearchQuery(value)}
        placeholder="Capacity component name or id"
      />
      <div className="d-flex gap-2 mt-2">
        {Boolean(filters.tsps?.length) === false && (
          <select
            className="form-select form-select-sm d-flex"
            value={selectedTspId}
            onChange={(event) => setSelectedTspId(event.target.value)}
          >
            <option value="">All TSPs</option>
            {Object.values(tsps)
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              .map((tsp) => (
                <option key={tsp.id} value={String(tsp.id)}>
                  {tsp.name}
                </option>
              ))}
          </select>
        )}
        <select
          className="form-select form-select-sm d-flex"
          value={selectedCapacityType}
          onChange={(event) =>
            setSelectedCapacityType(
              event.target.value as '' | 'POINT' | 'SEGMENT',
            )
          }
        >
          <option value="">All Types</option>
          <option value="POINT">POINT</option>
          <option value="SEGMENT">SEGMENT</option>
        </select>
      </div>
      {(searchQuery.length > 0 ||
        selectedTspId.length > 0 ||
        selectedCapacityType !== '') && (
        <CapacityComponentSearchResults
          filters={filters}
          onSelect={onSelect}
          textSearch={debouncedSearchQuery}
          selectedTspId={selectedTspId}
          selectedCapacityType={selectedCapacityType}
          debouncing={searchQuery != debouncedSearchQuery}
        />
      )}
    </ReusableModal>
  );
};

type CapacityComponentSearchResultsProps = {
  textSearch: string;
  onSelect: (component: CapacityComponent) => void;
  debouncing: boolean;
  filters: Partial<AdvancedFiltersState['capacityComponents']>;
  selectedTspId: string;
  selectedCapacityType: '' | 'POINT' | 'SEGMENT';
};

const CapacityComponentSearchResults: React.FC<
  CapacityComponentSearchResultsProps
> = ({
  onSelect,
  textSearch,
  debouncing,
  filters,
  selectedTspId,
  selectedCapacityType,
}) => {
  const { data: components, isFetching } = useGetCapacityComponentsQuery({
    ...filters,
    textSearch,
    tsps: selectedTspId ? [Number(selectedTspId)] : [],
    capacityTypes: selectedCapacityType ? [selectedCapacityType] : [],
    includeLinkHealth: false,
  });

  if (isFetching || debouncing) {
    return <LoadingList />;
  }

  if (Object.keys(components ?? {}).length === 0) {
    return (
      <ul className="list-group list-group-flush">
        <li className="list-group-item">No Results</li>
      </ul>
    );
  }

  return (
    <div className="d-flex flex-column align-content-center pt-3">
      {Object.values(components ?? {}).map((component) => (
        <div
          key={component.id}
          role="button"
          onClick={() => onSelect(component)}
          className="list-group-item p-2 border-bottom"
        >
          <div className="d-flex justify-content-between align-items-start gap-3">
            <div>
              <div className="fw-semibold">{component.label}</div>
              <div className="text-muted small">
                #{component.id} · {component.capacity_type} ·{' '}
                {component.tsp_name || 'Unknown TSP'}
              </div>
            </div>
            <span className="text-muted small">
              Locations: {component?.points?.length || 0}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};
